import React, { useState, useEffect } from 'react';
import db from "./Firebase";
import { useNavigate, Link } from 'react-router-dom';

const MONTHS = [
    { label: 'January', start: new Date('2026-01-01'), end: new Date('2026-02-01') },
    { label: 'February', start: new Date('2026-02-01'), end: new Date('2026-03-01') },
    { label: 'March', start: new Date('2026-03-01'), end: new Date('2026-04-01') },
];

function AnalyticsPr() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [portfolios, setPortfolios] = useState([]);
    const [loans, setLoans] = useState([]);
    const [orders, setOrders] = useState([]);
    const [downloads, setDownloads] = useState([]);

    useEffect(() => {
        const email = localStorage.getItem("email");
        const password = localStorage.getItem("password");
        const remember = localStorage.getItem("remember");
        if (remember === "false" || !email || !password) navigate('/signin');
    }, []);

    useEffect(() => {
        const jan = new Date('2026-01-01');
        const apr = new Date('2026-04-01');

        const unsubs = [
            db.collection("nv_portfolio_details")
                .where("created_date", ">=", jan)
                .where("created_date", "<", apr)
                .onSnapshot(snap => setPortfolios(snap.docs.map(d => d.data()))),

            db.collection("nv_loan")
                .where("created_date", ">=", jan)
                .where("created_date", "<", apr)
                .onSnapshot(snap => setLoans(snap.docs.map(d => d.data()))),

            db.collection("nv_orders_data")
                .where("order_date", ">=", jan)   // ← was created_date
                .where("order_date", "<", apr)
                .onSnapshot(snap => setOrders(snap.docs.map(d => d.data()))),

            db.collection("personal_details")
                .where("created_date", ">=", jan)
                .where("created_date", "<", apr)
                .onSnapshot(snap => {
                    setDownloads(snap.docs.map(d => d.data()));
                    setLoading(false);
                }),
        ];

        return () => unsubs.forEach(u => u());
    }, []);

    // filter helpers
    const inMonth = (item, month) =>
        item.created_date?.toDate() >= month.start &&
        item.created_date?.toDate() < month.end;

    const stats = MONTHS.map(month => ({
        label: month.label,
        created: portfolios.filter(p => inMonth(p, month)).length,
        verified: portfolios.filter(p => inMonth(p, month) && p.is_published === "yes").length,
        applied: loans.filter(l => inMonth(l, month)).length,
        disbursed: loans.filter(l => inMonth(l, month) && l.final_status === "Disbursed").length,
        orders: orders.filter(o => {
            const d = o.order_date?.toDate();
            return d >= month.start && d < month.end;
        }).length,
        downloads: downloads.filter(d => inMonth(d, month)).length,
    }));

    const totals = stats.reduce((acc, m) => ({
        created: acc.created + m.created,
        verified: acc.verified + m.verified,
        applied: acc.applied + m.applied,
        disbursed: acc.disbursed + m.disbursed,
        orders: acc.orders + m.orders,
        downloads: acc.downloads + m.downloads,
    }), { created: 0, verified: 0, applied: 0, disbursed: 0, orders: 0, downloads: 0 });

    const Card = ({ label, value, sub }) => (
        <div style={{
            background: 'var(--bs-white, #fff)', border: '1px solid #e9ecef',
            borderRadius: 10, padding: '1rem 1.25rem'
        }}>
            <p style={{ fontSize: 12, color: '#6c757d', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</p>
            <p style={{ fontSize: 28, fontWeight: 600, margin: 0 }}>{value}</p>
            <p style={{ fontSize: 12, color: '#6c757d', margin: '4px 0 0' }}>{sub}</p>
        </div>
    );

    const MonthTable = ({ rows, columns }) => (
        <div style={{ overflowX: 'auto' }}>
            <table className="table table-bordered table-sm" style={{ fontSize: 13 }}>
                <thead className="thead-dark">
                    <tr>
                        <th></th>
                        {MONTHS.map(m => <th key={m.label} className="text-center">{m.label}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {rows.map(row => (
                        <tr key={row.key}>
                            <td style={{ fontWeight: 500 }}>{row.label}</td>
                            {stats.map(m => (
                                <td key={m.label} className="text-center">{m[row.key]}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    return (
        <main className="main-content position-relative max-height-vh-100 h-100 border-radius-lg">
            <div className="p-2" style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: 5 }}>
                <Link to="/"><img src="/images/menubutton.png" className="menu-toggle" alt="Menu" style={{ height: '2rem' }} /></Link>
                <h6 className="mb-0 mt-1">Analytics Preview · Jan–Mar 2026</h6>
            </div>

            {loading ? (
                <div className="p-4 text-center text-muted">Loading data…</div>
            ) : (
                <div className="p-2">

                    {/* Summary cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
                        <Card label="Portfolios created" value={totals.created} sub="Jan–Mar" />
                        <Card label="Portfolios verified" value={totals.verified} sub='is_published = "yes"' />
                        <Card label="RG applied" value={totals.applied} sub="Jan–Mar" />
                        <Card label="RG disbursed" value={totals.disbursed} sub='final_status = "Disbursed"' />
                        <Card label="Grow packages" value={totals.orders} sub="Jan–Mar" />
                        <Card label="App downloads" value={totals.downloads} sub="Jan–Mar" />
                    </div>

                    {/* Portfolios */}
                    <div className="card mb-3">
                        <div className="card-header pb-0"><h6 className="mb-0">Portfolios</h6></div>
                        <div className="card-body pt-2">
                            <MonthTable rows={[
                                { key: 'created', label: 'Created' },
                                { key: 'verified', label: 'Verified' },
                            ]} />
                        </div>
                    </div>

                    {/* RG Loans */}
                    <div className="card mb-3">
                        <div className="card-header pb-0"><h6 className="mb-0">RG Loans</h6></div>
                        <div className="card-body pt-2">
                            <MonthTable rows={[
                                { key: 'applied', label: 'Applied' },
                                { key: 'disbursed', label: 'Disbursed' },
                            ]} />
                        </div>
                    </div>

                    {/* Grow packages */}
                    <div className="card mb-3">
                        <div className="card-header pb-0"><h6 className="mb-0">Grow Packages</h6></div>
                        <div className="card-body pt-2">
                            <MonthTable rows={[{ key: 'orders', label: 'Orders' }]} />
                        </div>
                    </div>

                    {/* App downloads */}
                    <div className="card mb-3">
                        <div className="card-header pb-0"><h6 className="mb-0">App Downloads</h6></div>
                        <div className="card-body pt-2">
                            <MonthTable rows={[{ key: 'downloads', label: 'Downloads' }]} />
                        </div>
                    </div>

                </div>
            )}
        </main>
    );
}

export default AnalyticsPr;